import { useEffect, useRef, useState, type RefObject } from 'react';

import type { SelectionSharedState } from '@atlaskit/editor-common/selection';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension';

/** Minimal shape we need: optional selection with optional empty (ProseMirror Selection has .empty). */
type SelectionState = { selection?: { empty?: boolean } | null } | undefined;

/**
 * Editor API with Selection plugin's sharedState (currentState / onChange).
 * PublicPluginAPI<[SelectionExtensionPlugin]> doesn't declare this; we extend it so we can
 * subscribe to selection changes and read selection.empty for "meaningful selection".
 */
export type SelectionAwareEditorAPI = PublicPluginAPI<[SelectionExtensionPlugin]> & {
	selection?: {
		sharedState?: {
			currentState?: () => SelectionState;
			onChange?: (
				callback: (props: {
					nextSharedState: SelectionSharedState;
					prevSharedState: SelectionSharedState;
				}) => void,
			) => (() => void) | void;
		};
	};
};

const hasMeaningfulSelection = (
	selectionState: SelectionState,
	editorApi: SelectionAwareEditorAPI | null | undefined,
) => {
	const selection =
		selectionState?.selection ?? editorApi?.selection?.sharedState?.currentState?.()?.selection;

	return Boolean(selection && !selection.empty);
};

/**
 * Returns true once the user has made a meaningful selection in the editor, so that external
 * (Forge & Connect) toolbar extensions can be fetched. Becomes true only once per (contentId, isEnabled);
 * repeated selections do not re-fire the query. On page transition (contentId change), the gate resets
 * and the query can run again for the new page after the first selection there.
 */
export const useShouldFetchExternalExtensions = ({
	contentId,
	isEnabled,
	editorAPIRef,
}: {
	contentId: string | undefined;
	isEnabled: boolean;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
}) => {
	const [shouldFetchExternalExtensions, setShouldFetchExternalExtensions] =
		useState<boolean>(false);
	const [subscriptionRetryCount, setSubscriptionRetryCount] = useState<number>(0);
	const previousContentIdRef = useRef<string | undefined>(contentId);
	const previousIsEnabledRef = useRef<boolean>(isEnabled);

	// Reset the fetch gate when contentId or isEnabled changes (e.g. page transition).
	// This allows the query to run once after first selection on the new page.
	useEffect(() => {
		if (previousContentIdRef.current !== contentId || previousIsEnabledRef.current !== isEnabled) {
			setShouldFetchExternalExtensions(false);
			previousContentIdRef.current = contentId;
			previousIsEnabledRef.current = isEnabled;
		}
	}, [contentId, isEnabled]);

	// Wait for editor API, then set the fetch gate to true once: either immediately if there is
	// already a meaningful selection, or on first selection change. Early-return when the gate is
	// already true so repeated selections do not re-fire the external extensions query.
	useEffect(() => {
		if (!isEnabled || shouldFetchExternalExtensions) {
			return;
		}

		const resolvedEditorApi = editorAPIRef.current as SelectionAwareEditorAPI | null | undefined;

		if (!resolvedEditorApi) {
			// The editor ref can still be null/undefined while the editor is mounting, so we retry on
			// the next tick to give the ref a chance to be populated and the selection subscription to attach.
			const retryTimer = setTimeout(() => {
				setSubscriptionRetryCount((currentRetryCount) => currentRetryCount + 1);
			}, 0);

			return () => {
				clearTimeout(retryTimer);
			};
		}

		if (hasMeaningfulSelection(undefined, resolvedEditorApi)) {
			setShouldFetchExternalExtensions(true);
			return;
		}

		// Return the unsubscribe from onChange so React runs it on cleanup (unmount or deps change).
		return resolvedEditorApi.selection?.sharedState?.onChange?.(({ nextSharedState }) => {
			if (hasMeaningfulSelection(nextSharedState, resolvedEditorApi)) {
				setShouldFetchExternalExtensions(true);
			}
		});
	}, [editorAPIRef, contentId, isEnabled, shouldFetchExternalExtensions, subscriptionRetryCount]);

	return shouldFetchExternalExtensions;
};
